import { DataTable } from "@bcc/components/data-table";
import type { DataTableRow } from "@client-shared";
import type { LabelPrintBatch } from "@shared/types";
import * as React from "react";
import type { Row, SortingState } from "@tanstack/react-table";
import { createBatchesColumns } from "./columns";
import { setBatchesTableContainerTestId } from "./batches-table.utils";

export interface BatchesTableProps {
  data: LabelPrintBatch[];
  globalFilter?: string;
  isLoading?: boolean;
  selectedBatch?: string | null;
  onSelectBatch?: (batchId: string) => void;
  testId?: string;
}

const DEFAULT_SORTING: SortingState = [{ id: "createdAt", desc: true }];

export const BatchesTable = React.memo(function BatchesTable({
  data,
  globalFilter,
  isLoading = false,
  selectedBatch,
  onSelectBatch,
  testId,
}: BatchesTableProps) {
  const [sorting, setSorting] = React.useState<SortingState>(DEFAULT_SORTING);

  const columns = React.useMemo(() => createBatchesColumns({ onSelectBatch }), [onSelectBatch]);
  const tableData = React.useMemo(
    () =>
      data.map(
        (batch) =>
          ({
            ...batch,
            id: batch.batchId,
          }) as DataTableRow<LabelPrintBatch> & { id: string }
      ),
    [data]
  );
  const rowSelection = React.useMemo(() => (selectedBatch ? { [selectedBatch]: true } : {}), [selectedBatch]);

  const handleRowClick = React.useCallback(
    (row: Row<DataTableRow<LabelPrintBatch>>) => {
      onSelectBatch?.(row.original.batchId);
    },
    [onSelectBatch]
  );

  return (
    <div data-testid={testId ?? setBatchesTableContainerTestId()}>
      <DataTable
        data={tableData}
        columns={columns}
        rowSelection={rowSelection}
        sorting={sorting}
        onSortingChange={setSorting}
        searchable={false}
        globalFilter={globalFilter}
        isLoading={isLoading}
        onRowClick={handleRowClick}
      />
    </div>
  );
});
